import { getI18n } from '@/plugins/i18n/index'
import { themeConfig } from '@themeConfig'
import { useI18n } from 'vue-i18n'
import { createVuetify } from 'vuetify'
import { VBtn } from 'vuetify/components/VBtn'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import defaults from './defaults'
import { icons } from './icons'

// Force primary color
const FORCED_PRIMARY_COLOR = '#6ba8e1'
const FORCED_PRIMARY_DARKEN_COLOR = '#4a91d9'

// Background color settings
const BACKGROUND_COLOR = '#eee'

// Styles
import '@core-scss/template/libs/vuetify/index.scss'
import 'vuetify/styles'

export default function (app) {
  // Override cookie values
  const setCookie = (name, value) => {
    document.cookie = `${name}=${value}; path=/; max-age=31536000` // 1 year
  }
  
  // Force save these values to cookies
  setCookie('lightThemePrimaryColor', FORCED_PRIMARY_COLOR)
  setCookie('darkThemePrimaryColor', FORCED_PRIMARY_COLOR)
  setCookie('lightThemePrimaryDarkenColor', FORCED_PRIMARY_DARKEN_COLOR)
  setCookie('darkThemePrimaryDarkenColor', FORCED_PRIMARY_DARKEN_COLOR)

  // Create forced themes that will override everything
  // Must include darken-1 variants for all colors — the button SCSS uses them on :hover
  const forcedThemes = {
    light: {
      dark: false,
      colors: {
        'primary': FORCED_PRIMARY_COLOR,
        'primary-darken-1': FORCED_PRIMARY_DARKEN_COLOR,
        'secondary-darken-1': '#016E6D',
        'info-darken-1': '#1E88E5',
        'warning-darken-1': '#E27E00',
        'success-darken-1': '#43A047',
        'error-darken-1': '#E53935',
        'background': BACKGROUND_COLOR,
      },
    },
    dark: {
      dark: true,
      colors: {
        'primary': FORCED_PRIMARY_COLOR,
        'primary-darken-1': FORCED_PRIMARY_DARKEN_COLOR,
        'secondary-darken-1': '#016E6D',
        'info-darken-1': '#1E88E5',
        'warning-darken-1': '#E27E00',
        'success-darken-1': '#43A047',
        'error-darken-1': '#E53935',
        'background': BACKGROUND_COLOR,
      },
    },
  }
  
  // Use a simpler theme option structure to avoid complex merging
  const themeOptions = {
    defaultTheme: resolveVuetifyTheme(themeConfig.app.theme),
    themes: forcedThemes,
  }

  const vuetify = createVuetify({
    aliases: {
      IconBtn: VBtn,
    },
    defaults,
    icons,
    theme: themeOptions,
    locale: {
      adapter: createVueI18nAdapter({ i18n: getI18n(), useI18n }),
    },
  })

  // Create the elevation styles
  const elevationStyles = generateElevationStyles()

  // Also add CSS variables as a fallback
  const style = document.createElement('style')

  style.innerHTML = `
    :root {
      --v-theme-primary: ${FORCED_PRIMARY_COLOR} !important;
      --v-theme-primary-darken-1: ${FORCED_PRIMARY_DARKEN_COLOR} !important;
      --v-theme-background: ${BACKGROUND_COLOR} !important;
    }

    /* Fix for box shadows */
    ${elevationStyles}
  `
  document.head.appendChild(style)

  app.use(vuetify)
}

// Helper function for theme resolution
function resolveVuetifyTheme(theme) {
  return theme === 'system' ? 'light' : theme
}

// Generate elevation styles for box shadows
function generateElevationStyles() {
  // Define shadow constants
  const umbra = 'rgba(0, 0, 0, 0.1)'
  const penumbra = 'rgba(0, 0, 0, 0.08)'
  const ambient = 'rgba(0, 0, 0, 0.06)'

  // Elevation definitions for box-shadows (copied from Vuetify's internal definitions)
  const elevations = {
    0: '0px 0px 0px 0px ${umbra},0px 0px 0px 0px ${penumbra},0px 0px 0px 0px ${ambient}',
    1: '0px 2px 1px -1px ${umbra},0px 1px 1px 0px ${penumbra},0px 1px 3px 0px ${ambient}',
    2: '0px 3px 1px -2px ${umbra},0px 2px 2px 0px ${penumbra},0px 1px 5px 0px ${ambient}',
    3: '0px 3px 3px -2px ${umbra},0px 3px 4px 0px ${penumbra},0px 1px 8px 0px ${ambient}',
    4: '0px 2px 4px -1px ${umbra},0px 4px 5px 0px ${penumbra},0px 1px 10px 0px ${ambient}',
    5: '0px 3px 5px -1px ${umbra},0px 5px 8px 0px ${penumbra},0px 1px 14px 0px ${ambient}',
    6: '0px 3px 5px -1px ${umbra},0px 6px 10px 0px ${penumbra},0px 1px 18px 0px ${ambient}',
    7: '0px 4px 5px -2px ${umbra},0px 7px 10px 1px ${penumbra},0px 2px 16px 1px ${ambient}',
    8: '0px 5px 5px -3px ${umbra},0px 8px 10px 1px ${penumbra},0px 3px 14px 2px ${ambient}',
    9: '0px 5px 6px -3px ${umbra},0px 9px 12px 1px ${penumbra},0px 3px 16px 2px ${ambient}',
    10: '0px 6px 6px -3px ${umbra},0px 10px 14px 1px ${penumbra},0px 4px 18px 3px ${ambient}',
    11: '0px 6px 7px -4px ${umbra},0px 11px 15px 1px ${penumbra},0px 4px 20px 3px ${ambient}',
    12: '0px 7px 8px -4px ${umbra},0px 12px 17px 2px ${penumbra},0px 5px 22px 4px ${ambient}',
    13: '0px 7px 8px -4px ${umbra},0px 13px 19px 2px ${penumbra},0px 5px 24px 4px ${ambient}',
    14: '0px 7px 9px -4px ${umbra},0px 14px 21px 2px ${penumbra},0px 5px 26px 4px ${ambient}',
    15: '0px 8px 9px -5px ${umbra},0px 15px 22px 2px ${penumbra},0px 6px 28px 5px ${ambient}',
    16: '0px 8px 10px -5px ${umbra},0px 16px 24px 2px ${penumbra},0px 6px 30px 5px ${ambient}',
    17: '0px 8px 11px -5px ${umbra},0px 17px 26px 2px ${penumbra},0px 6px 32px 5px ${ambient}',
    18: '0px 9px 11px -5px ${umbra},0px 18px 28px 2px ${penumbra},0px 7px 34px 6px ${ambient}',
    19: '0px 9px 12px -6px ${umbra},0px 19px 29px 2px ${penumbra},0px 7px 36px 6px ${ambient}',
    20: '0px 10px 13px -6px ${umbra},0px 20px 31px 3px ${penumbra},0px 8px 38px 7px ${ambient}',
    21: '0px 10px 13px -6px ${umbra},0px 21px 33px 3px ${penumbra},0px 8px 40px 7px ${ambient}',
    22: '0px 10px 14px -6px ${umbra},0px 22px 35px 3px ${penumbra},0px 8px 42px 7px ${ambient}',
    23: '0px 11px 14px -7px ${umbra},0px 23px 36px 3px ${penumbra},0px 9px 44px 8px ${ambient}',
    24: '0px 11px 15px -7px ${umbra},0px 24px 38px 3px ${penumbra},0px 9px 46px 8px ${ambient}',
  }

  // Generate CSS for all elevation classes
  let css = ''
  for (let i = 0; i <= 24; i++) {
    // Replace the template variables in the box-shadow string
    const boxShadow = elevations[i]
      .replace(/\$\{umbra\}/g, umbra)
      .replace(/\$\{penumbra\}/g, penumbra)
      .replace(/\$\{ambient\}/g, ambient)
    
    // Add the elevation class
    css += `
      .elevation-${i} {
        box-shadow: ${boxShadow} !important;
      }
      .v-elevation-${i} {
        box-shadow: ${boxShadow} !important;
      }
    `
  }

  // Shadow for elevation level 4 (commonly used for navigation bars)
  const navbarShadow = elevations[4]
    .replace(/\$\{umbra\}/g, umbra)
    .replace(/\$\{penumbra\}/g, penumbra)
    .replace(/\$\{ambient\}/g, ambient)

  // Define background color
  const bgColor = '#eee'

  // Add direct override for specific components that need shadows
  css += `
  body {
    background: ${bgColor} !important;
  }
  
  /* Target Vuetify containers to override their white background */
  .v-application, 
  .v-application__wrap, 
  .v-main, 
  .v-container,
  .v-sheet {
    background-color: ${bgColor} !important;
  }

  .v-row + .v-row {
  margin-top: 4px;
  }
  
  .v-overlay__scrim {
  background-color: rgba(0, 0, 0, 0.5) !important;
  }
  
  .v-card {
    box-shadow: ${elevations[2].replace(/\$\{umbra\}/g, umbra).replace(/\$\{penumbra\}/g, penumbra).replace(/\$\{ambient\}/g, ambient)} !important;
  }
  .v-app-bar {
    box-shadow: ${elevations[4].replace(/\$\{umbra\}/g, umbra).replace(/\$\{penumbra\}/g, penumbra).replace(/\$\{ambient\}/g, ambient)} !important;
  }
  .navbar-content-container, .v-overlay__content, .layout-vertical-nav {
    box-shadow: ${navbarShadow} !important;
  }
  
  /* Remove shadows from buttons */
  .v-btn {
    box-shadow: none !important;
  }
  .v-btn--elevated {
    box-shadow: none !important;
  }
  `

  return css
}
